import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { Datasource } from './datasources';

/** Where a resolved external-table definition came from. */
export type ExternalDefinitionOrigin =
    | 'stored'
    | 'engine_view_ddl'
    | 'synthesized';

/** Response of `external-tables/definition/:tableId`. */
export interface ExternalTableDefinition {
    tableId: number;
    value: string;
    label?: string;
    type: string;
    is_custom: boolean;
    sourceType: string;
    /** Always a SELECT. Reusable as a custom definition unless the flag below says otherwise. */
    definition: string;
    origin: ExternalDefinitionOrigin;
    isCustomDefinitionCompatible: boolean;
    /** Raw engine DDL when one was retrieved; informational only. */
    engineDefinition: string | null;
    /** The definition with the data source's filters wrapped around it. */
    queriedDefinition: string | null;
    appliedFilters: Record<string, string[]>;
    warnings: string[];
}

export interface PeiResult {
    rowCount: number;
    executionTimeSec: number;
    pei: number;
    grade: string;
    label: string;
}

/** Named resource in a data-source usage bucket (`items` are capped, usually at 50). */
export interface DataSourceUsageItem {
    id: string;
    name: string;
}

/** Exact `total` plus a capped list of named items. */
export interface DataSourceUsageBucket {
    total: number;
    items: DataSourceUsageItem[];
}

/**
 * Response of `GET data_source/:eid/usage` — where the source is referenced
 * and whether it can currently be deleted.
 */
export interface DataSourceUsage {
    canDelete: boolean;
    blockers: {
        agents: DataSourceUsageBucket;
        tables: DataSourceUsageBucket;
        uploads: DataSourceUsageBucket;
        externalTables: DataSourceUsageBucket;
        formTables: DataSourceUsageBucket;
        trackers: DataSourceUsageBucket;
        procedures: DataSourceUsageBucket;
        mediaFiles: DataSourceUsageBucket;
        activeBulkOperations: DataSourceUsageBucket;
    };
}

@Injectable({ providedIn: 'root' })
export class DataSourceService {
    private apiLink = environment.apiLink;
    private _httpClient = inject(HttpClient);

    constructor() {}

    /** GET data_source/data-sources-by-role - sources visible to the given owner scope. */
    getByRoleScope(roleId: number, role: string): Observable<Datasource[]> {
        const params = new HttpParams()
            .set('roleId', String(roleId))
            .set('role', role);
        return this._httpClient
            .get(this.apiLink + 'data_source/data-sources-by-role', { params })
            .pipe(
                map((res: any) => {
                    const data = res?.data;
                    const raw =
                        data?.data_sources ??
                        data?.sources ??
                        data?.dataSources ??
                        (Array.isArray(data) ? data : []);
                    const sources = Array.isArray(raw) ? raw : [];
                    return sources.map((ds: any) => ({
                        ...ds,
                        eid: ds.eid ?? ds.id,
                    }));
                })
            );
    }

    getAll(page, size, name?: string, datatype?, department?, form?) {
        // Calculate offset from page number: page 0 = offset 0, page 1 = offset size, etc.
        const offset = page;

        let params = new HttpParams();
        params = params.append('offset', offset);
        params = params.append('limit', size);

        if (name) {
            params = params.append('name', name);
        }
        if (datatype) {
            params = params.append('datatype', datatype);
        }
        if (department) {
            params = params.append('departmentId', department);
        }
        if(form){
            params = params.append('datatype', form);
        }
        return this._httpClient
            .get(this.apiLink + 'data_source/data-sources', { params })
            .pipe(
                map((res: any) => {
                    return {
                        data: res.data.sources,
                        count: res.data.count,
                        can_create_data_source: res.data.can_create_data_source,
                        can_use_snowflake: res.data.can_use_snowflake,
                        can_use_sap: res.data.can_use_sap
                            ? res.data.can_use_sap
                            : false,
                    };
                })
            );
    }

    getExternalTablesInDatasource(id, departmentId?: number | null) {
        let params = new HttpParams();
        if (departmentId != null) {
            params = params.append('departmentId', String(departmentId));
        }
        return this._httpClient.get(
            this.apiLink + `external-tables/get-tables/${id}`,
            { params }
        );
    }

    /**
     * Reads the linked external tables out of a `getExternalTablesInDatasource`
     * response. The API still nests them under `data_srouce.sap` - both the typo
     * and the SAP name predate multi-engine support. Keeping that knowledge here
     * means the eventual API rename is a one-line change instead of a hunt
     * through every caller.
     */
    externalTablesFromResponse(res: any): any[] {
        return res?.data?.data_srouce?.sap ?? [];
    }

    /** The data source object from the same response. */
    dataSourceFromExternalTablesResponse(res: any): any {
        return res?.data?.data_srouce;
    }

    /**
     * One data source by public eid.
     * Backend route is singular: GET /v1/data_source/:eid/data-source
     * There is no GET /v1/data_source/:eid/data-sources.
     */
    getById(id: string | number, name?: string): Observable<Datasource> {
        let params = new HttpParams();
        if (name) {
            params = params.append('name', name);
        }
        return this._httpClient
            .get<{ data?: { source?: Datasource } }>(
                this.apiLink + `data_source/${id}/data-source`,
                { params },
            )
            .pipe(
                map(res => res.data?.source as Datasource)
            );
    }

    getTablesInDatasource(id: any, name?: string) {
        let params = new HttpParams();
        if (name) {
            params = params.append('name', name);
        }
        return this._httpClient
            .get(this.apiLink + `data_source/${id}/data-sources/tables`, {
                params,
            })
            .pipe(
                map((res: any) => {
                    return {
                        tables: res.data.tables,
                        count: res.data.count,
                        can_create_table: res.data.can_create_table,
                        dataSource: res.data.dataSource,
                        isMyDataSource: res.data.isMyDataSource,
                    };
                })
            );
    }

    getAllTablesInDatasource(
        id: any,
        name?: string,
        offset?: number,
        limit?: number
    ) {
        let params = new HttpParams();
        if (name) {
            params = params.append('name', name);
        }
        if (offset != null) {
            params = params.append('offset', String(offset));
        }
        if (limit != null) {
            params = params.append('limit', String(limit));
        }
        return this._httpClient
            .get(this.apiLink + `data_source/${id}/get-tables`, { params })
            .pipe(
                map((res: any) => {
                    return {
                        tables: res.data.tables,
                        count:
                            typeof res.data.count === 'number'
                                ? res.data.count
                                : null,
                    };
                })
            );
    }

    /** GET data_source/{id}/get-all-tables-and-meta-data - internal + external tables and metadata. */
    getAllTablesAndMetadata(dataSourceId: string | number) {
        return this._httpClient.get(
            this.apiLink + `data_source/${dataSourceId}/get-all-tables-and-meta-data`
        );
    }

    getAvailableExternalTables(sourceType) {
        let params = new HttpParams();
        if (sourceType) {
            params = params.append('sourceType', sourceType);
        }
        return this._httpClient.get(
            `${this.apiLink}external-tables/get-tables`,
            { params }
        );
    }

    getAllColumns(tableName, sourceType) {
        let params = new HttpParams();
        if (tableName) {
            params = params.append('tableName', tableName);
        }
        if (sourceType) {
            params = params.append('sourceType', sourceType);
        }
        return this._httpClient
            .get(this.apiLink + 'external-tables/get-columns', { params })
            .pipe(
                map((res: any) => {
                    return {
                        columns: res.data.columns,
                    };
                })
            );
    }

    addExternalTable(modal) {
        return this._httpClient.post(
            `${this.apiLink}external-tables/add-table`,
            modal
        );
    }

    getFilter(tableName, filterId, departmentId?: number | null) {
        let params = new HttpParams()
            .append('tableName', tableName)
            .append('filterId', filterId);
        if (departmentId != null) {
            params = params.append('departmentId', String(departmentId));
        }
        return this._httpClient.get(
            `${this.apiLink}external-tables/get-filters`,
            { params }
        );
    }

    assignExternalTablesToDatasource(modal) {
        return this._httpClient.post(
            `${this.apiLink}external-tables/assign-filters`,
            modal
        );
    }

    addFilterToExternalTable(modal) {
        return this._httpClient.post(
            `${this.apiLink}external-tables/add-new-filter`,
            modal
        );
    }

    /** GET external-tables/master-filter/{eid} - shared columns across all linked tables + current master filters. */
    getMasterFilterContext(dataSourceId: string | number, departmentId?: number | null) {
        let params = new HttpParams();
        if (departmentId != null) {
            params = params.append('departmentId', String(departmentId));
        }
        return this._httpClient
            .get(`${this.apiLink}external-tables/master-filter/${dataSourceId}`, { params })
            .pipe(
                map((res: any) => {
                    // Responses are wrapped in the standard { success, data, ... } envelope.
                    const d = res?.data ?? res;
                    return {
                        sharedColumns: d.sharedColumns ?? [],
                        tableCount: d.tableCount ?? 0,
                        tables: d.tables ?? [],
                        current: d.current ?? {},
                    };
                })
            );
    }

    /** POST external-tables/apply-master-filter - apply a master filter across all tables sharing the column(s). */
    applyMasterFilter(modal: {
        dataSourceId: string | number;
        departmentId?: number | null;
        filters: { column: string; filters: string[] }[];
    }) {
        return this._httpClient
            .post(`${this.apiLink}external-tables/apply-master-filter`, modal)
            .pipe(map((res: any) => res?.data ?? res));
    }

    /** PUT external-tables/remove-master-filter - remove all master filters (or a single column) for the data source. */
    removeMasterFilter(modal: {
        dataSourceId: string | number;
        departmentId?: number | null;
        column?: string;
    }) {
        return this._httpClient
            .put(`${this.apiLink}external-tables/remove-master-filter`, modal)
            .pipe(map((res: any) => res?.data ?? res));
    }

    /**
     * POST data_source/upload-as-custom-definition - upload a gzipped tabular payload (multipart
     * field `file`) into an external data source as an inline custom external-table definition.
     * Response: { tables: [...] } (refreshed external custom-tables list for that sourceType).
     */
    uploadAsCustomDefinition(formData: FormData) {
        return this._httpClient.post(
            this.apiLink + 'data_source/upload-as-custom-definition',
            formData
        );
    }

    editExternalTable(modal, id) {
        return this._httpClient.put(
            `${this.apiLink}external-tables/edit-table/${id}`,
            modal
        );
    }

    deleteExternalTable(id) {
        return this._httpClient.delete(
            `${this.apiLink}external-tables/delete-table/${id}`
        );
    }

    getDocumentsInDatasource(id: string | number, name?: string) {
        let params = new HttpParams();
        if (name) {
            params = params.append('name', name);
        }
        return this._httpClient
            .get(this.apiLink + `data_source/${id}/data-sources/uploads`, {
                params,
            })
            .pipe(
                map((res: any) => {
                    return {
                        uploads: res.data.uploads,
                        count: res.data.count,
                        can_create_upload: res.data.can_create_upload,
                        dataSource: res.data.dataSource,
                        isMyDataSource: res.data.isMyDataSource,
                        folders: res.data.folders ?? [],
                    };
                })
            );
    }

    generateExternalTableDescription(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/generate-table-desc',
            modal
        );
    }

    create(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/new-data-sources',
            modal
        );
    }

    update(modal: any, id: number) {
        return this._httpClient.put(
            this.apiLink + `data_source/${id}/edit-data-sources`,
            modal
        );
    }

    delete(id: number | string) {
        return this._httpClient.delete(
            this.apiLink + `data_source/${id}/delete-data-sources`
        );
    }

    /**
     * Where this data source is used (agents, tables, trackers, …) and whether
     * it can be deleted. Called on demand from the Usage dialog — not on list load.
     */
    getUsage(eid: string | number): Observable<DataSourceUsage> {
        return this._httpClient
            .get<{ data: DataSourceUsage }>(this.apiLink + `data_source/${eid}/usage`)
            .pipe(map(res => res.data));
    }

    removeFilter(modal) {
        return this._httpClient.put(
            this.apiLink + 'external-tables/remove-filter',
            modal
        );
    }

    getAsCsv(tableName: string) {
        const token = localStorage.getItem('accessToken');

        let params = new HttpParams().set('tableName', tableName);
        if (token) {
            params = params.set('token', token);
        }

        return this._httpClient.get(
            this.apiLink + 'data_source/get-as-csv',
            { params, responseType: 'blob' }
        );
    }

    /**
     * Export external table (SAP/Oracle/Snowflake/Remote Postgres) as CSV.
     * Either: date range (dateCol, startDate, endDate) - no limit/offset;
     * or: pagination (limit, offset) when no date column is used.
     */
    exportExternalAsCsv(params: {
        dataSourceId: string | number;
        tableName: string;
        dateCol?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
    }) {
        let httpParams = new HttpParams()
            .set('dataSourceId', String(params.dataSourceId))
            .set('tableName', params.tableName);
        if (params.dateCol && params.startDate && params.endDate) {
            httpParams = httpParams
                .set('dateCol', params.dateCol)
                .set('startDate', params.startDate)
                .set('endDate', params.endDate);
        } else if (params.limit != null && params.offset != null) {
            httpParams = httpParams
                .set('limit', String(params.limit))
                .set('offset', String(params.offset));
        }
        return this._httpClient.get(
            this.apiLink + 'data_source/export-external-as-csv',
            { params: httpParams, responseType: 'blob' }
        );
    }

    /** POST data_source/get-metrics-and-date - external CSV export dialog only. */
    getDataSourceMetricsAndDate(modal: Record<string, unknown>) {
        return this._httpClient
            .post(this.apiLink + 'data_source/get-metrics-and-date', modal)
            .pipe(
                map((res: any) => ({
                    metrics: res.data.metricColumns,
                    dates: res.data.dateColumns,
                    dimensionColumns: res.data.dimensionColumns,
                    totalCount: res.data.totalCount,
                    threshold: res.data.threshold,
                }))
            );
    }

    /** POST data_source/get-min-max-date - external CSV export dialog only. */
    getDataSourceMinMaxDate(modal: Record<string, unknown>) {
        return this._httpClient.post(
            this.apiLink + 'data_source/get-min-max-date',
            modal
        );
    }

    editCustomDefinition(payload: { tableId: string; definition: string }) {
        return this._httpClient.put(
            `${this.apiLink}external-tables/edit-custom-view-definition`,
            payload
        );
    }

    /**
     * GET external-tables/definition/:tableId - the raw SQL definition of any external
     * table (stored custom SQL, peeled engine view DDL, or a generated projection).
     *
     * Passing `dataSourceId` adds the "as queried" variant, since the filters that shape
     * the read live on the table-to-data-source link rather than on the table.
     */
    getExternalTableDefinition(
        tableId: number,
        dataSourceId?: string
    ): Observable<ExternalTableDefinition> {
        let params = new HttpParams();
        if (dataSourceId) {
            params = params.set('dataSourceId', dataSourceId);
        }
        return this._httpClient
            .get<{ data: ExternalTableDefinition }>(
                `${this.apiLink}external-tables/definition/${tableId}`,
                { params }
            )
            // The API wraps every success body in `{ success, data, statusCode, message }`
            // (the i18n plugin's onSend hook), so the payload must be unwrapped here.
            .pipe(map(res => res.data));
    }

    generateDatasourceDescription(dataSourceId: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/generate-datasource-description',
            { dataSourceId }
        );
    }

    getTablePerformance(dataSourceId: string, tableName: string, sourceType?: string): Observable<{ data: PeiResult }> {
        let params = new HttpParams()
            .set('dataSourceId', dataSourceId)
            .set('tableName', tableName);
        if (sourceType) {
            params = params.set('sourceType', sourceType);
        }
        return this._httpClient.get<{ data: PeiResult }>(
            this.apiLink + 'data_source/table-performance',
            { params }
        );
    }

    /** PUT data_source/{id}/share-departments - sync shared department list. */
    shareWithDepartments(
        dataSourceId: string | number,
        departmentIds: number[]
    ): Observable<unknown> {
        return this._httpClient.put(
            `${this.apiLink}data_source/${dataSourceId}/share-departments`,
            { departmentIds }
        );
    }

    /**
     * GET data_source/internal-table-filter-context
     * Authoritative columns + per-department normal filters for one UserTable.
     */
    getInternalTableFilterContext(tableId: number): Observable<{
        tableId: number;
        tableName: string;
        dataSourceId: string | null;
        columns: string[];
        departments: Array<{
            id: number | null;
            name: string | null;
            about: string | null;
            description: string | null;
            generatedAt: unknown;
            filters: Array<{
                id?: number;
                col: string;
                filter: string;
                isMaster: boolean;
            }>;
        }>;
    }> {
        const params = new HttpParams().set('tableId', String(tableId));
        return this._httpClient
            .get(`${this.apiLink}data_source/internal-table-filter-context`, { params })
            .pipe(map((res: any) => res?.data ?? res));
    }

    /** GET data_source/internal-table-filters — one department scope. */
    getInternalTableFilters(tableId: number, departmentId?: number | null) {
        let params = new HttpParams().set('tableId', String(tableId));
        if (departmentId != null) {
            params = params.set('departmentId', String(departmentId));
        }
        return this._httpClient
            .get(`${this.apiLink}data_source/internal-table-filters`, { params })
            .pipe(map((res: any) => res?.data ?? res));
    }

    /** POST data_source/replace-internal-table-filters — single scope. */
    replaceInternalTableFilters(body: {
        tableId: number;
        departmentId?: number | null;
        filters: { column: string; filters: string[] }[];
    }) {
        return this._httpClient
            .post(`${this.apiLink}data_source/replace-internal-table-filters`, body)
            .pipe(map((res: any) => res?.data ?? res));
    }

    /**
     * POST data_source/bulk-replace-internal-table-filters
     * One transaction for the listed department scopes (dirty-only is fine).
     */
    bulkReplaceInternalTableFilters(body: {
        tableId: number;
        departments: Array<{
            departmentId: number | null;
            filters: { column: string; filters: string[] }[];
        }>;
    }): Observable<{ done: boolean }> {
        return this._httpClient
            .post(`${this.apiLink}data_source/bulk-replace-internal-table-filters`, body)
            .pipe(map((res: any) => (res?.data ?? res) as { done: boolean }));
    }

    /** GET data_source/internal-master-filter/{eid} — shared Internal columns + current masters. */
    getInternalMasterFilterContext(
        dataSourceId: string | number,
        departmentId?: number | null
    ) {
        let params = new HttpParams();
        if (departmentId != null) {
            params = params.append('departmentId', String(departmentId));
        }
        return this._httpClient
            .get(`${this.apiLink}data_source/internal-master-filter/${dataSourceId}`, {
                params,
            })
            .pipe(
                map((res: any) => {
                    const d = res?.data ?? res;
                    return {
                        sharedColumns: d.sharedColumns ?? [],
                        tableCount: d.tableCount ?? 0,
                        tables: d.tables ?? [],
                        current: d.current ?? {},
                    };
                })
            );
    }

    /** POST data_source/apply-internal-master-filter */
    applyInternalMasterFilter(modal: {
        dataSourceId: string | number;
        departmentId?: number | null;
        filters: { column: string; filters: string[] }[];
    }) {
        return this._httpClient
            .post(`${this.apiLink}data_source/apply-internal-master-filter`, modal)
            .pipe(map((res: any) => res?.data ?? res));
    }

    /** PUT data_source/remove-internal-master-filter */
    removeInternalMasterFilter(modal: {
        dataSourceId: string | number;
        departmentId?: number | null;
        column?: string;
    }) {
        return this._httpClient
            .put(`${this.apiLink}data_source/remove-internal-master-filter`, modal)
            .pipe(map((res: any) => res?.data ?? res));
    }

    /**
     * POST data_source/bulk-apply-internal-master-filter
     * Atomic multi-department Internal master filter save.
     */
    bulkApplyInternalMasterFilter(body: {
        dataSourceId: string | number;
        departments: Array<{
            departmentId: number | null;
            filters: { column: string; filters: string[] }[];
        }>;
    }): Observable<{
        done: boolean;
        affectedTables: string[];
        affectedDepartmentIds: Array<number | null>;
    }> {
        return this._httpClient
            .post(`${this.apiLink}data_source/bulk-apply-internal-master-filter`, body)
            .pipe(
                map(
                    (res: any) =>
                        (res?.data ?? res) as {
                            done: boolean;
                            affectedTables: string[];
                            affectedDepartmentIds: Array<number | null>;
                        }
                )
            );
    }
}
